import { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Form } from 'react-bootstrap';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import * as Yup from 'yup';
import { Dropdown, Modal, TextInput } from '@pmi/dsm-react';
import { toastWithAnalytics } from '../../../../../../shared/analytics';
import { SelectOption } from '../../../../../../Hub/TrainingHub/EnrollmentCard/ProductModal/ProfileUpdateModal/models';
import { useUpdateAdmin2FANumberMutation } from './apiSlice';
import { Admin2FAPhoneNumberModel } from '../../models';

interface EditPhoneNumberModalProps {
  id: number;
  countries: SelectOption[];
  phoneNumber?: Admin2FAPhoneNumberModel;
  onCancel?: () => void;
  phoneNumberSchema: any;
}

interface FormModel {
  phoneNumber: Admin2FAPhoneNumberModel;
  id: number;
}

export default function EditPhoneNumberModal({
  id,
  phoneNumber = undefined,
  phoneNumberSchema,
  countries,
  onCancel = () => {},
}: EditPhoneNumberModalProps) {
  const [updateAdminPhoneNumber, { isLoading }] =
    useUpdateAdmin2FANumberMutation();

  const countryRegionCodeOptions: Option[] =
    countries?.map((pn) => ({
      label: pn.text,
      value: pn.value,
    })) ?? [];

  const [options, setOptions] = useState(countryRegionCodeOptions);

  const initialData: FormModel = {
    phoneNumber,
    id,
  };
  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.object().shape({
      countryCodeId: Yup.string().required('Country/Region code is required'),
      number: Yup.string()
        .required('Phone number is required')
        .matches(
          phoneNumberSchema?.properties?.number?.pattern,
          'Must be a valid phone number',
        ),
    }),
  });

  const handleInputChange = (_: any, value: string, currentValue?: string) => {
    const newOptions = countryRegionCodeOptions.filter(
      (option) =>
        option.label
          .toLocaleLowerCase()
          .startsWith(value.toLocaleLowerCase()) ||
        option.value === currentValue,
    );
    setOptions(newOptions);
  };

  const onSave = async (
    values: FormModel,
    action: FormikHelpers<FormModel>,
  ) => {
    action.setSubmitting(true);

    const countryCode = countryRegionCodeOptions
      .find(
        (x) =>
          x.value.toString() === values.phoneNumber.countryCodeId.toString(),
      )
      .label.split(' ')
      .pop();

    updateAdminPhoneNumber({
      id,
      phoneNumberRequest: {
        ...values.phoneNumber,
        countryCode,
      },
    })
      .unwrap()
      .then(() => {
        toastWithAnalytics(`Phone number updated successfully`, 'success');
        action.setSubmitting(false);
        onCancel();
      })
      .catch((ex) => {
        toastWithAnalytics(
          `Error while updating phone Number !! ${JSON.stringify(
            ex?.data?.errors,
          )}.`,
          'danger',
        );
      })
      .finally(() => {
        action.setSubmitting(false);
      });
  };

  const onCancelHandler = () => {
    onCancel();
  };

  return (
    <Formik
      initialValues={initialData}
      validateOnChange
      validateOnBlur
      validationSchema={validationSchema}
      onSubmit={(values, action) => {
        onSave(values, action);
      }}
    >
      {({
        submitForm,
        dirty,
        errors,
        setFieldValue,
        handleChange,
        values,
        initialValues,
        isValid,
        isSubmitting,
      }) => (
        <Modal
          show
          customClassName="modalBody100"
          headline={`${phoneNumber ? 'Edit' : 'Add'} Phone Number`}
          description="The phone number is needed for an extra layer of security called Two-Factor Authentication (2FA). It helps ensure that only authorized users can access the software and protects against unauthorized access."
          onClose={onCancelHandler}
          buttons={[
            {
              titleText: 'Cancel',
              variant: 'outline-secondary',
              onClick: onCancelHandler,
              disabled: isLoading,
            },
            {
              titleText: 'Save Changes',
              variant: 'primary',
              onClick: submitForm,
              disabled:
                !dirty ||
                !isValid ||
                values === initialValues ||
                isLoading ||
                isSubmitting,
            },
          ]}
        >
          <div className="d-flex flex-column mb-5">
            <Form className="mb-5">
              <div className="row gy-3 row-cols-1 mb-5">
                <div className="col-6 mb-3">
                  <Form.Group
                    controlId="phoneNumber.countryCodeId"
                    className={
                      errors?.phoneNumber?.countryCodeId ? 'is-invalid' : null
                    }
                  >
                    <Dropdown
                      className="cursor-pointer"
                      onChange={(e, value) => {
                        setFieldValue('phoneNumber.countryCodeId', value);
                        setOptions(countryRegionCodeOptions);
                      }}
                      onChangeInput={(_, value) =>
                        handleInputChange(
                          _,
                          value,
                          values.phoneNumber?.countryCodeId,
                        )
                      }
                      label="Country/Region Code"
                      options={options}
                      invalid={!!errors.phoneNumber?.countryCodeId}
                      placeholder="Select"
                      value={values.phoneNumber?.countryCodeId?.toString()}
                    />
                    {errors.phoneNumber?.countryCodeId ? (
                      <div className="invalid-feedback">
                        {errors.phoneNumber?.countryCodeId}
                      </div>
                    ) : null}
                  </Form.Group>
                </div>

                <div className="col-6 mb-3">
                  <Form.Group
                    controlId="phoneNumber.number"
                    className={
                      errors?.phoneNumber?.number ? 'is-invalid' : null
                    }
                  >
                    <Form.Label className="mb-2">Number</Form.Label>
                    <TextInput
                      name="phoneNumber.number"
                      maxLength={
                        phoneNumberSchema?.properties?.number?.maxLength
                      }
                      onChange={handleChange}
                      value={values?.phoneNumber?.number}
                      isInvalid={!!errors?.phoneNumber?.number}
                      className={
                        errors?.phoneNumber?.number ? 'is-invalid' : null
                      }
                      validationMessage={errors?.phoneNumber?.number}
                    />
                  </Form.Group>
                </div>
              </div>
            </Form>
          </div>
        </Modal>
      )}
    </Formik>
  );
}
